import {
    asWidgetChartLimit,
    JsonChartLimit,
    JsonColor,
    JsonDescription,
    JsonWidgetDataAvailability,
    mapColor,
    mapDataAvailability,
    mapDate,
    mapDescription,
} from 'adapter/out/client/widgets/data/mappers/common';
import { WidgetType } from 'domain/entity/widget';
import { PeriodType } from 'domain/entity/widgetData';
import GaugeData, { WidgetGaugeComparison, WidgetGaugeRange, WidgetGaugeIndicator } from 'domain/entity/widgets/GaugeData';

// WidgetType.GAUGE
export type JsonGaugeTooltip = {
    text: string;
};

export type JsonGaugeRange = {
    color: JsonColor;
    from: number;
    to: number;
    name: string;
};

export type JsonGaugeComparison = {
    enabled: boolean;
    period: {
        type: PeriodType;
        text: string;
        from: string;
        to: string;
    };
    color: JsonColor;
    value: number;
    ['show_as_percentage']: boolean;
};

export type JsonGaugeIndicator = {
    color: JsonColor;
    value: number;
    name?: string;
};

export type JsonWidgetGaugeData = {
    type: WidgetType;
    description: JsonDescription;
    data: {
        checkpoints: JsonChartLimit[];
        comparison: JsonGaugeComparison;
        indicator: JsonGaugeIndicator;
        min: number;
        max: number;
        ranges: JsonGaugeRange[];
        ['show_as_percentage']: boolean;
        type: string;
        units: string;
    };
    ['data_availability']: JsonWidgetDataAvailability;
};

const asWidgetGaugeComparison = (json: JsonGaugeComparison): WidgetGaugeComparison =>
    new WidgetGaugeComparison(
        mapColor(json.color),
        json.enabled,
        {
            type: json.period.type,
            from: json.period.from ? mapDate(json.period.from) : null,
            to: json.period.to ? mapDate(json.period.to) : null,
            text: json.period.text,
        },
        json.show_as_percentage,
        json.value
    );

const asWidgetGaugeIndicator = (json: JsonGaugeIndicator): WidgetGaugeIndicator =>
    new WidgetGaugeIndicator(mapColor(json.color), json.value, json.name);

export const asWidgetGaugeRanges = (jsons: JsonGaugeRange[]): WidgetGaugeRange[] =>
    jsons.map((json) => new WidgetGaugeRange(mapColor(json.color), json.from, json.to, json.name));

export const asGaugeWidget = (json: JsonWidgetGaugeData): GaugeData =>
    new GaugeData(
        asWidgetChartLimit(json.data.checkpoints),
        asWidgetGaugeComparison(json.data.comparison),
        mapDataAvailability(json.data_availability),
        mapDescription(json.description),
        json.data.type,
        asWidgetGaugeIndicator(json.data.indicator),
        json.data.max,
        json.data.min,
        asWidgetGaugeRanges(json.data.ranges),
        json.data.show_as_percentage,
        json.data.units
    );
